import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { AuthGuard } from '../auth/AuthGuard';
import { AppShell } from './components/AppShell';

function GuestOnlyRoute({ element }: { element: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <>{element}</>;
}

const AuthCallbackView = lazy(() => import('./views/AuthCallbackView'));
const AuthErrorView = lazy(() => import('./views/AuthErrorView'));
const LoginView = lazy(() => import('./views/LoginView'));
const DashboardView = lazy(() => import('./views/DashboardView'));
const SettingsView = lazy(() => import('./views/SettingsView'));
const PreferencesView = lazy(() => import('./views/PreferencesView'));
const I18nDemoView = lazy(() => import('./views/I18nDemoView'));

export function AppRouter() {
  return (
    <Routes>
      {/* Auth routes — public, no shell, no guard */}
      <Route path="/login" element={<GuestOnlyRoute element={<LoginView />} />} />
      <Route path="/auth/callback" element={<AuthCallbackView />} />
      <Route path="/auth/error" element={<AuthErrorView />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <AuthGuard>
            <AppShell>
              <Navigate to="/dashboard" replace />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/dashboard"
        element={
          <AuthGuard>
            <AppShell>
              <DashboardView />
            </AppShell>
          </AuthGuard>
        }
      />

      {/* i18n demo */}
      <Route
        path="/i18n"
        element={
          <AuthGuard>
            <AppShell>
              <I18nDemoView />
            </AppShell>
          </AuthGuard>
        }
      />

      {/* Settings */}
      <Route
        path="/settings"
        element={
          <AuthGuard>
            <AppShell>
              <SettingsView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/settings/:section"
        element={
          <AuthGuard>
            <AppShell>
              <SettingsView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/settings/:section/:subSection"
        element={
          <AuthGuard>
            <AppShell>
              <SettingsView />
            </AppShell>
          </AuthGuard>
        }
      />

      {/* User preferences */}
      <Route
        path="/preferences"
        element={
          <AuthGuard>
            <AppShell>
              <PreferencesView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/preferences/:section"
        element={
          <AuthGuard>
            <AppShell>
              <PreferencesView />
            </AppShell>
          </AuthGuard>
        }
      />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default AppRouter;
